import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, LayersIcon, ChevronDown, ChevronRight, Zap, Check, Tag,
} from "lucide-react";
import * as api from "../../lib/api";
import { PRIMARY, OPPORTUNITY_COLORS, fmt, usePersistentState } from "./shared";
import { VERDICTS, VerdictChip, Sparkline, BackfillCard, AuthorityChart, verdictDetail } from "./ClusterAuthority";
import { COVERAGE_COLORS, Chip, ClusterArticles, PriorityMetrics } from "./TopicParts";
import SearchTopics from "./SearchTopics";

const CLUSTER_COLORS = [
  "#018D4C", "#FF2A00", "#FF8C00", "#9B59B6", "#1ABC9C",
  "#E67E22", "#3498DB", "#E74C3C", "#2ECC71", "#8E44AD",
  "#16A085", "#D35400", "#2980B9", "#27AE60", "#0055FF",
];

// Weeks of history needed before the 4-vs-4-week verdict means anything.
const MIN_HISTORY_WEEKS = 8;
const CHART_LINES = 5;

// Same "Topic N" fallback the backend relabel job looks for.
const PLACEHOLDER_LABEL = /^Topic \d+$/;

function Step({ n, label, done, active, action }) {
  return (
    <div className="flex items-center gap-2.5 flex-1 min-w-0 px-4 py-3">
      <span
        className="w-5 h-5 flex-shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold"
        style={done
          ? { background: PRIMARY, color: "#fff" }
          : active
            ? { background: "#fff", color: PRIMARY, border: `1.5px solid ${PRIMARY}` }
            : { background: "#F4F4F5", color: "#A1A1AA" }}
      >
        {done ? <Check size={11} strokeWidth={3} /> : n}
      </span>
      <span className={`text-xs font-bold truncate ${done || active ? "text-zinc-800" : "text-zinc-400"}`}>{label}</span>
      {action && <span className="ml-auto flex-shrink-0">{action}</span>}
    </div>
  );
}

function SetupStepper({ gscConnected, analyzed, gapTopics, onGoToQueries }) {
  const linkCls = "text-[11px] font-bold hover:underline";
  return (
    <div className="flex flex-col sm:flex-row sm:divide-x divide-zinc-100 bg-white border border-zinc-100 rounded-xl mb-4">
      <Step
        n={1}
        label="Connect Search Console"
        done={gscConnected}
        active={!gscConnected}
        action={!gscConnected && <a href="/dashboard/settings/search-console" className={linkCls} style={{ color: PRIMARY }}>Connect →</a>}
      />
      <Step
        n={2}
        label="Run Gap Analysis"
        done={analyzed}
        active={gscConnected && !analyzed}
        action={gscConnected && !analyzed && <button onClick={onGoToQueries} className={linkCls} style={{ color: PRIMARY }}>Run →</button>}
      />
      <Step
        n={3}
        label={analyzed ? `Strengthen ${gapTopics} topic${gapTopics === 1 ? "" : "s"} with gaps` : "Strengthen topic clusters"}
        done={false}
        active={gscConnected && analyzed}
      />
    </div>
  );
}

function ClusterQueries({ clusterId, total, onOpenQueries }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.getPerformanceQueries({ cluster_id: clusterId, sort: "impressions", order: "desc", limit: 10 })
      .then(r => setRows(r.queries || []))
      .catch(() => setRows([]));
  }, [clusterId]);

  return (
    <div>
      <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">Search demand</p>
      {rows === null ? (
        <p className="text-xs text-zinc-400 py-2">Loading queries…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-zinc-400 py-2">No matched queries. Run Gap Analysis to map GSC queries to this topic.</p>
      ) : (
        <div className="space-y-1">
          {rows.map(q => {
            const oc = OPPORTUNITY_COLORS[q.opportunity];
            return (
              <button
                key={q.id}
                onClick={() => navigate(`/dashboard/query-gaps/${q.id}`)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-zinc-100 hover:border-zinc-300 rounded-lg text-left"
              >
                <span className="text-xs text-zinc-900 truncate flex-1">{q.query}</span>
                <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
                  {fmt(q.impressions)} impr · pos {fmt(q.position, 1)}
                </span>
                {oc && <Chip bg={oc.bg} text={oc.text}>{q.opportunity.replace(/_/g, " ")}</Chip>}
              </button>
            );
          })}
        </div>
      )}
      {total > 0 && (
        <button onClick={() => onOpenQueries(clusterId)} className="mt-2 text-[11px] font-bold hover:underline" style={{ color: PRIMARY }}>
          View all {fmt(total)} queries in this topic →
        </button>
      )}
    </div>
  );
}

// Ranked gaps for a topic that isn't strengthening — what to do, most direct first.
function NextMoves({ topic, authority, onOpenQueries, onFanOut }) {
  const verdict = authority?.verdict;
  if (verdict === "strengthening") {
    return (
      <p className="text-[11px] text-green-700 mb-4">
        <strong>Gaining authority. Keep publishing into this topic.</strong> {verdictDetail(authority)}
      </p>
    );
  }
  const optimize = topic.underperforming_queries - topic.quick_wins;
  const moves = [
    topic.missing_queries > 0 && {
      key: "write",
      text: `Write for ${topic.missing_queries} search${topic.missing_queries === 1 ? "" : "es"} with no ranking page`,
      why: "Missing subtopics: Google sees demand in this topic you don't answer yet.",
      cta: "See queries", onClick: () => onOpenQueries(topic.cluster_id),
    },
    topic.quick_wins > 0 && {
      key: "quick",
      text: `Push ${topic.quick_wins} quick win${topic.quick_wins === 1 ? "" : "s"} from position 11–30 to page 1`,
      why: "Closest to moving top-10 share: update the ranking page or add internal links.",
      cta: "See queries", onClick: () => onOpenQueries(topic.cluster_id),
    },
    optimize > 0 && {
      key: "optimize",
      text: `Optimize ${optimize} page${optimize === 1 ? "" : "s"} ranking past position 30`,
      why: "Weak pages drag the topic down; deeper rewrites or consolidation.",
      cta: "See queries", onClick: () => onOpenQueries(topic.cluster_id),
    },
    topic.queries === 0 && {
      key: "expand",
      text: "No search demand matched yet: find the subtopics people search for",
      why: "Fan-out the topic to see which queries this cluster should be ranking for.",
      cta: "Fan-Out", onClick: () => onFanOut(topic.topic),
    },
  ].filter(Boolean);
  if (moves.length === 0) return null;
  return (
    <div className="mb-4">
      <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-400 mb-2">
        {verdict === "weakening" ? "Losing authority: gaps to close" : "Next moves to grow authority"}
      </p>
      <ol className="space-y-1">
        {moves.map((m, i) => (
          <li key={m.key} className="flex items-center gap-3 px-3 py-2 bg-white border border-zinc-100 rounded-lg">
            <span className="w-4 text-[10px] font-bold text-zinc-400 tabular-nums">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-zinc-800">{m.text}</p>
              <p className="text-[10px] text-zinc-400">{m.why}</p>
            </div>
            <button onClick={m.onClick} className="text-[11px] font-bold hover:underline whitespace-nowrap" style={{ color: PRIMARY }}>
              {m.cta} →
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClusterRow({ topic, color, authority, onOpenQueries, onFanOut }) {
  const [expanded, setExpanded] = useState(false);
  const placeholder = PLACEHOLDER_LABEL.test(topic.topic || "");
  const cov = COVERAGE_COLORS[topic.coverage] || { bg: "#F4F4F5", text: "#52525B" };

  return (
    <div className="border-b border-zinc-100 last:border-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setExpanded(v => !v))}
        className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5 hover:bg-zinc-50 cursor-pointer"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1 basis-60">
          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
          <div className="min-w-0">
            <p className={`text-sm font-bold truncate ${placeholder ? "text-zinc-400 italic" : "text-zinc-900"}`}>
              {topic.topic}
              {placeholder && <span className="ml-1.5 not-italic text-[9px] font-bold uppercase tracking-wide text-amber-600">name pending</span>}
            </p>
            <p className="text-[11px] text-zinc-400 mt-0.5">
              {fmt(topic.existing_pages)} articles
              {topic.authority_score != null && <> · authority {Math.round(topic.authority_score)}</>}
              {topic.key_page_queries > 0 && <span className="text-green-700 font-bold"> · {topic.key_page_queries} on key pages</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 text-right tabular-nums">
          {authority !== undefined && (
            <div className="flex flex-col items-end gap-1" title={verdictDetail(authority)}>
              <Sparkline series={authority?.series} color={color} />
              <span className="text-[9px] uppercase tracking-wide text-zinc-400">ranking queries</span>
            </div>
          )}
          <PriorityMetrics priority={topic.priority} impressions={topic.impressions} clicks={topic.clicks} />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap justify-end basis-56">
          {authority !== undefined && <VerdictChip authority={authority} />}
          {topic.queries === 0 ? (
            <Chip bg="#F4F4F5" text="#71717A" title="No GSC queries matched to this topic yet">no demand yet</Chip>
          ) : (
            <>
              {topic.missing_queries > 0 && (
                <Chip {...OPPORTUNITY_COLORS.missing_coverage} title="Queries with impressions but no ranking page — write new content">
                  {topic.missing_queries} to write
                </Chip>
              )}
              {topic.quick_wins > 0 && (
                <Chip {...OPPORTUNITY_COLORS.quick_win} title="Underperforming queries at position 11–30">
                  {topic.quick_wins} quick wins
                </Chip>
              )}
              {topic.underperforming_queries - topic.quick_wins > 0 && (
                <Chip {...OPPORTUNITY_COLORS.growth_opportunity} title="Underperforming queries past position 30 — optimize existing pages">
                  {topic.underperforming_queries - topic.quick_wins} to optimize
                </Chip>
              )}
            </>
          )}
          {topic.coverage && <Chip bg={cov.bg} text={cov.text} title="Content depth grade (size, words, cohesion), not Google's verdict">{topic.coverage}</Chip>}
          <button
            onClick={e => { e.stopPropagation(); onFanOut(topic.topic); }}
            disabled={placeholder}
            title={placeholder ? "Wait for the topic to be named" : "Run query fan-out for this topic"}
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border hover:opacity-80 disabled:opacity-30"
            style={{ background: "#ECFDF5", color: PRIMARY, borderColor: "#A7F3D0" }}
          >
            <Zap size={10} /> Fan-Out
          </button>
          {expanded ? <ChevronDown size={14} className="text-zinc-400" /> : <ChevronRight size={14} className="text-zinc-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
          <NextMoves topic={topic} authority={authority} onOpenQueries={onOpenQueries} onFanOut={onFanOut} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ClusterQueries clusterId={topic.cluster_id} total={topic.queries} onOpenQueries={onOpenQueries} />
            <ClusterArticles clusterId={topic.cluster_id} />
          </div>
        </div>
      )}
    </div>
  );
}

const VIEWS = [
  { key: "search",   label: "Search topics",    title: "Topics grouped from your GSC queries. Works without a CMS" },
  { key: "clusters", label: "Content clusters", title: "Topics grouped from your synced articles" },
];

// Two views of topical authority: demand-side search topics (from queries) and
// supply-side content clusters (from CMS articles). Search topics are the default
// because they exist as soon as Gap Analysis has run, CMS or not.
export default function TopicClustersTab({ onOpenQueries }) {
  const [view, setView] = usePersistentState("topics.view", "search");
  return (
    <div>
      <div className="flex gap-0.5 mb-4 bg-zinc-100 rounded-lg p-0.5 w-fit">
        {VIEWS.map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            title={v.title}
            className="px-2.5 py-1 text-[11px] font-bold rounded-md whitespace-nowrap"
            style={view === v.key
              ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
              : { color: "#71717a" }}
          >
            {v.label}
          </button>
        ))}
      </div>
      {view === "clusters"
        ? <ContentClusters onOpenQueries={onOpenQueries} />
        : <SearchTopics onGoToQueries={() => onOpenQueries(null)} />}
    </div>
  );
}

function ContentClusters({ onOpenQueries }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [gsc, setGsc] = useState(null);
  const [gapSummary, setGapSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relabeling, setRelabeling] = useState(false);
  const [authority, setAuthority] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState("all");
  const pollRef = useRef(null);
  const backfillPollRef = useRef(null);

  const loadAuthority = useCallback(
    () => api.getClusterAuthority({ weeks: 26 }).then(setAuthority).catch(e => { console.error(e); return null; }),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    const [map, settings, gaps] = await Promise.all([
      api.getContentGapMap({ limit: 100 }).catch(e => { console.error(e); return null; }),
      api.getGscSettings().catch(() => null),
      api.getQueryGaps().catch(() => null),
      loadAuthority(),
    ]);
    setData(map);
    setGsc(settings);
    setGapSummary(gaps?.summary || null);
    setLoading(false);
  }, [loadAuthority]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(backfillPollRef.current); }, []);

  // Poll while a backfill runs (also resumes after a reload mid-backfill).
  const backfillRunning = authority?.backfill?.status === "running";
  useEffect(() => {
    if (!backfillRunning) return;
    backfillPollRef.current = setInterval(loadAuthority, 4000);
    return () => clearInterval(backfillPollRef.current);
  }, [backfillRunning, loadAuthority]);

  const handleBackfill = async () => {
    try {
      const r = await api.backfillClusterAuthority();
      setAuthority(a => ({ ...(a || {}), backfill: r.state }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setAuthority(a => ({ ...(a || {}), backfill: { status: "failed", message: msg } }));
    }
  };

  const handleRelabel = async () => {
    setRelabeling(true);
    try {
      await api.startRelabel();
      pollRef.current = setInterval(async () => {
        try {
          const st = await api.getSyncStatus();
          if (st.status === "running") return;
        } catch {}
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRelabeling(false);
        load();
      }, 3000);
    } catch (e) {
      console.error(e);
      setRelabeling(false);
    }
  };

  const topics = data?.topics || [];
  const gscConnected = !!(gsc?.configured && gsc?.verified);
  const analyzed = !!gapSummary;
  const gapTopics = topics.filter(t => t.missing_queries + t.underperforming_queries > 0).length;
  const placeholders = topics.filter(t => PLACEHOLDER_LABEL.test(t.topic || "")).length;

  const hasHistory = (authority?.weeks_stored || 0) >= MIN_HISTORY_WEEKS;
  // undefined = no history yet (row hides the trend), null = history but no traffic to this cluster.
  const authorityOf = (cid) => (hasHistory ? authority.clusters?.[cid] || null : undefined);
  const verdictOf = (cid) => authorityOf(cid)?.verdict || "insufficient_data";
  const colorOf = Object.fromEntries(topics.map((t, i) => [t.cluster_id, CLUSTER_COLORS[i % CLUSTER_COLORS.length]]));
  const verdictCounts = topics.reduce((acc, t) => ({ ...acc, [verdictOf(t.cluster_id)]: (acc[verdictOf(t.cluster_id)] || 0) + 1 }), {});
  const shown = hasHistory && verdictFilter !== "all" ? topics.filter(t => verdictOf(t.cluster_id) === verdictFilter) : topics;
  const chartLines = shown
    .filter(t => authority?.clusters?.[t.cluster_id])
    .slice(0, CHART_LINES)
    .map(t => ({ id: t.cluster_id, name: t.topic, color: colorOf[t.cluster_id] }));

  return (
    <div>
      {!loading && (
        <SetupStepper
          gscConnected={gscConnected}
          analyzed={analyzed}
          gapTopics={gapTopics}
          onGoToQueries={() => onOpenQueries(null)}
        />
      )}

      {!loading && gscConnected && !hasHistory && (
        <BackfillCard state={authority?.backfill} onStart={handleBackfill} weeksStored={authority?.weeks_stored || 0} />
      )}

      {hasHistory && chartLines.length > 0 && <AuthorityChart authority={authority} lines={chartLines} />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        {hasHistory ? (
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {["all", "weakening", "stalled", "strengthening", "insufficient_data"].map(key => {
              const count = key === "all" ? topics.length : verdictCounts[key] || 0;
              if (key !== "all" && count === 0) return null;
              return (
                <button
                  key={key}
                  onClick={() => setVerdictFilter(key)}
                  className="px-2.5 py-1 text-[10px] font-bold rounded-md whitespace-nowrap"
                  style={verdictFilter === key
                    ? { background: "#fff", color: "#18181b", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }
                    : { color: "#71717a" }}
                >
                  {key === "all" ? "All" : VERDICTS[key].label} ({count})
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-zinc-400">
            {data ? `${data.total_topics} topic clusters · ranked by business priority of their search demand` : "Loading…"}
          </p>
        )}
        <div className="flex items-center gap-1">
          {placeholders > 0 && (
            <button
              onClick={handleRelabel}
              disabled={relabeling}
              title="Some topics still show placeholder names (Topic N) — generate names now"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50"
            >
              <Tag size={11} className={relabeling ? "animate-pulse" : ""} />
              {relabeling ? "Naming topics…" : `Name ${placeholders} topic${placeholders === 1 ? "" : "s"}`}
            </button>
          )}
          <button onClick={load} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors" title="Reload">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">
          Loading…
        </div>
      )}

      {!loading && topics.length === 0 && (
        <div className="px-4 py-8 text-center text-sm text-zinc-400 bg-white rounded-xl border border-zinc-100">
          No topic clusters yet. Sync your articles from Overview to build them.
        </div>
      )}

      {shown.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-100 shadow-sm mb-3">
          {shown.map(t => (
            <ClusterRow
              key={t.cluster_id}
              topic={t}
              color={colorOf[t.cluster_id]}
              authority={authorityOf(t.cluster_id)}
              onOpenQueries={onOpenQueries}
              onFanOut={label => navigate(`/dashboard/fanout?seed=${encodeURIComponent(label)}`)}
            />
          ))}
        </div>
      )}

      {data?.unmatched_queries > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 bg-blue-50 rounded-xl text-xs text-blue-800">
          <LayersIcon size={13} />
          <span>
            <strong>{fmt(data.unmatched_queries)}</strong> classified queries ({fmt(data.unmatched_impressions)} impressions) match no existing topic: demand for topics you don't cover yet.
          </span>
          <button onClick={() => onOpenQueries("none")} className="ml-auto font-bold hover:underline">
            Group into new topics →
          </button>
        </div>
      )}
    </div>
  );
}
